import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { BaseComponent } from '@ridango/core';
import { ModalController } from '@ionic/angular';
import { ApplicationFacade, FavoriteLocation, OpenTripPlannerFacade } from '@ridango/components';
import { combineLatest, Observable } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { FavoritesFacade, FavoriteStop } from '@ridango/components/lib/favorites';
import Env from '@ridango/core/env';
import { FavoriteSchedule } from '@ridango/components/lib/favorites/favorites.model';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'rwidget-favorites-list-modal',
    templateUrl: './favorites-list-modal.component.html',
    styleUrls: ['./favorites-list-modal.component.scss']
})
export class FavoritesListModalComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input() switchToNearbyTab: () => void;
    favoriteStops: FavoriteStop[] = [];
    allFavoriteStops: FavoriteStop[] = [];
    favoriteLocations: FavoriteLocation[] = [];
    allFavoriteLocations: FavoriteLocation[] = [];
    favoriteSchedules: FavoriteSchedule[] = [];
    allFavoriteSchedules: FavoriteSchedule[] = [];
    assetsPath$ = this.applicationFacade.assetsPath$;
    imageMapLocationPath = this.env.EXPLORE?.IMAGE_MAP_LOCATION_PATH || '/img/explore/location.svg';
    public readonly favoriteSchedules$: Observable<FavoriteSchedule[]> = this.favoritesFacade.favoriteSchedules$;
    isContrast$ = this.applicationFacade.highContrast$;

    constructor(
        @Inject('ENV') public env: Env,
        private applicationFacade: ApplicationFacade,
        private modalCtrl: ModalController,
        private favoritesFacade: FavoritesFacade,
        private openTripPlannerFacade: OpenTripPlannerFacade,
        private translateService: TranslateService
    ) {
        super();
    }

    ngOnInit(): void {
        this.favoritesFacade.favoriteLocations$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((locations: FavoriteLocation[]) => {
                this.favoriteLocations = locations;
                this.allFavoriteLocations = locations;
            });

        this.favoriteSchedules$.pipe(takeUntil(this.componentDestroyed$)).subscribe((schedules) => {
            this.favoriteSchedules = schedules;
            this.allFavoriteSchedules = schedules;
        });

        combineLatest(this.favoritesFacade.favoriteStops$, this.openTripPlannerFacade.stops$)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(([favorites, stops]) => {
                if (!stops?.length || !favorites?.length) {
                    return;
                }

                this.allFavoriteStops = favorites;
                this.favoriteStops = stops
                    .filter((stop) => !!favorites.find((favorite) => favorite.stopId === stop.id))
                    .map((stop) => {
                        const fav = favorites.find((f) => f.stopId === stop.id);

                        return { ...stop, stopId: fav.stopId, name: fav.alias || fav.name || stop.name };
                    });
            });
    }

    async cancel(): Promise<void> {
        await this.modalCtrl.dismiss(null, 'cancel');
    }

    handleDeleteFavoriteStop(item: FavoriteStop) {
        this.favoriteStops = this.favoriteStops.filter((f) => f.stopId !== item.stopId);
    }

    handleDeleteFavoriteLocation(location: FavoriteLocation) {
        this.favoriteLocations = this.favoriteLocations.filter(
            (l) => l.latitude !== location.latitude || l.longitude !== location.longitude
        );
    }

    public handleDeleteFavoriteSchedule(schedule: FavoriteSchedule): void {
        this.favoriteSchedules = this.favoriteSchedules.filter(
            (el) => el.stop?.id !== schedule.stop?.id || schedule?.id !== el?.id
        );
    }

    handleSaveClick() {
        const deletedFavoriteStopIds = this.allFavoriteStops
            .filter((f: FavoriteStop) => !this.favoriteStops.find((stop) => stop.stopId === f.stopId))
            .map((stop) => stop.stopId);

        const deletedFavoriteLocations = this.allFavoriteLocations.filter(
            (l: FavoriteLocation) =>
                !this.favoriteLocations.find(
                    (location) => l.latitude === location.latitude && l.longitude === location.longitude
                )
        );

        const deletedFavoriteSchedules = this.allFavoriteSchedules.filter(
            (favoriteSchedule: FavoriteSchedule) =>
                !this.favoriteSchedules.find(
                    (fs) =>
                        (fs.stopId === favoriteSchedule.stopId && fs.routeId === favoriteSchedule.routeId) ||
                        favoriteSchedule?.id === fs?.id
                )
        );

        this.favoritesFacade.deleteFavoriteSchedules(deletedFavoriteSchedules);
        this.favoritesFacade.deleteFavoriteStops(deletedFavoriteStopIds);
        this.favoritesFacade.deleteFavoriteLocations(deletedFavoriteLocations);

        this.modalCtrl.dismiss(null, 'cancel');

        if (this.areAllFavoritesEmpty() && this.switchToNearbyTab) {
            this.switchToNearbyTab();
        }
    }

    private areAllFavoritesEmpty(): boolean {
        return (
            this.favoriteStops.length === 0 &&
            this.favoriteLocations.length === 0 &&
            this.favoriteSchedules.length === 0
        );
    }

    get stopIconPath(): Observable<string> {
        return combineLatest([this.assetsPath$, this.isContrast$]).pipe(
            map(([path, isContrast]) => {
                const iconFile = this.env.EXPLORE?.USE_CONTRAST_ICON && isContrast ? 'bus-contrast.svg' : 'bus.svg';
                return `${path}/img/explore/${iconFile}`;
            })
        );
    }

    get useTrashIcon(): boolean {
        return this.env.EXPLORE?.USE_TRASH_BIN_FOR_REMOVE_FAVORITE_STOP === true;
    }

    shouldShowDivider(index: number, hasPreviousItems: boolean): boolean {
        return index !== 0 || hasPreviousItems;
    }

    getRemoveAriaLabel(itemName: string): string {
        return `${this.translateService.instant('REMOVE_FROM_FAVORITES')} ${itemName}`;
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }
}
